import type { BusinessSettingsModel } from '@/shared/generated'
import type { JSONSchema } from '@/shared/tools/json-schema-utils'

import { pipe } from 'lodash/fp'
import { CheckIcon, XIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useSettingsSchemasQuery } from '@/entities/core'

import {
	Item,
	ItemContent,
	ItemDescription,
	ItemMedia,
	ItemTitle,
} from '@/shared/components/ui/item'
import { Spinner } from '@/shared/components/ui/spinner'
import { cn } from '@/shared/lib/utils'
import {
	getResolvedSchema,
	translateSchemaKeys,
} from '@/shared/tools/json-schema-utils'

export function BusinessSettingsView({
	settings,
	className,
	compact = false,
}: {
	compact?: boolean
	settings?: BusinessSettingsModel
	className?: string
}) {
	const { t } = useTranslation()

	const schemaQuery = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema('part:business'),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})
	const isLoading = schemaQuery.isLoading

	return (
		<div className={cn('flex flex-col gap-2', className)}>
			{isLoading ? (
				<Spinner />
			) : (
				Object.entries(
					(schemaQuery.data?.properties || {}) as Record<string, JSONSchema>
				).map(([settingName, { title, measure }]) => {
					const settingValue = settings?.[settingName]

					return (
						<Item
							className={compact ? 'px-2 py-1' : ''}
							size='sm'
							variant='muted'
							key={settingName}
						>
							<ItemMedia>
								{settingValue ? (
									<CheckIcon
										className='size-4 shrink-0 rounded-lg bg-green-400 p-0.5 text-white dark:bg-green-800'
										strokeWidth={3}
									/>
								) : (
									<XIcon
										className='size-4 shrink-0 rounded-lg bg-red-400 p-0.5 text-white dark:bg-red-800'
										strokeWidth={3}
									/>
								)}
							</ItemMedia>
							<ItemContent className='flex-row gap-2'>
								<ItemTitle
									className={
										settingValue
											? 'font-semibold'
											: 'font-semibold text-muted-foreground'
									}
								>
									{title}
								</ItemTitle>
								<ItemDescription>
									{!!settingValue && Number.isFinite(settingValue)
										? ` ${settingValue} `
										: ''}
									{!!measure && !!settingValue ? t(measure) : ''}
								</ItemDescription>
							</ItemContent>
						</Item>
					)
				})
			)}
		</div>
	)
}
